import * as Sentry from '@sentry/nuxt'
import { Identify, createInstance } from '@amplitude/marketing-analytics-browser'

import type { AmplitudeOptions } from '@brighterly/lib-core-types/tools/amplitude/interfaces'
import type { Options } from '@amplitude/marketing-analytics-browser/lib/esm/typings/browser-client'

import { useCustomerStore } from '@stores/me/customer'
import { useDisciplineStore } from '@stores/discipline'
import { useKidStore } from '@stores/kid'

import { useKidInCustomer } from '@hooks/kid/kid-in-customer.hook'

// analytics tools

import { pick } from '@helpers/object'

import { getUserAgentInfo } from './user-agent'

export const amplitude = createInstance()

export const initAmplitude = (options: AmplitudeOptions = {}): void => {
  const store = useCustomerStore()

  const runtimeConfig = useRuntimeConfig().public
  const apiKey = runtimeConfig.amplitudeApiKey ?? ''
  const env = runtimeConfig.environment
  const serverUrl = runtimeConfig.amplitudeServerUrl
  const brand = runtimeConfig.brand

  if (!apiKey) {
    console.error('Error in method initAmplitude: amplitudeApiKey is not defined.')
  } else {
    if (!store._analyticsFirstSuccessInit) {
      setAmplitudeUserProperty('environment', env || '')
      setAmplitudeUserProperty('user_type', 'customer')
      setAmplitudeUserProperty('brand', brand)
    }

    const baseOptions: Options = {
      pageViewTracking: true,
      deviceId: store.customer?.device_id || options.deviceId || undefined,
    }

    if (serverUrl) {
      baseOptions.serverUrl = serverUrl
    }

    amplitude.init(
      apiKey,
      options.userId || undefined,
      baseOptions,
    )
      .promise
      .then((): void => {
        store._setAnalyticsFirstSuccessInit(true)
        const amplitudeId = store.customer?.device_id || options.deviceId || amplitude.getDeviceId()
        const amplitudeUrl = `https://app.amplitude.com/analytics/skelar/project/472284/search/${amplitudeId}`

        if (!store._analyticsFirstSuccessInit) {
          const dsn = useRuntimeConfig().public.sentryDsn
          if (dsn) {
            Sentry.setUser({
              amplitude_url: amplitudeUrl,
            })
          }
        }
      })
      .catch((): void => {
        const timeout = setTimeout(() => {
          initAmplitude(options)
          clearTimeout(timeout)
        }, 800)
      })
  }
}

export const trackEvent = (eventName: string, eventProps?: Record<string, any>, callback = (): void => {}): void => {
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()
  const kidStore = useKidStore()

  const {
    kid,
  } = useKidInCustomer({ kidId: kidStore.activeKidId! })

  const props = pick({
    customer_type: customerStore.customer?.type || null,
    source_funnel: customerStore.customer?.source_funnel || null,
    kid_id: kid.value?.id,
    kid_name: kid.value?.name,
    discipline_id: disciplineStore.activeDiscipline?.id,
    discipline_code: disciplineStore.activeDiscipline?.code,
    ...(eventProps || {}),
  })

  const hasProps = Object.keys(props).length > 0

  const deviceProps = getUserAgentInfo()
  const isCallback = (typeof callback === 'function')
  if (!hasProps) {
    amplitude
      .track(eventName, { ...deviceProps }).promise
      .finally(() => {
        if (isCallback) {
          callback()
        }
      })
  } else {
    amplitude
      .track(eventName, { ...deviceProps, ...props }).promise
      .finally(() => {
        if (isCallback) {
          callback()
        }
      })
  }
}

export const setAmplitudeUserProperty = (name: string, value: string, once: boolean = false): void => {
  const identify = new Identify()
  identify[once ? 'setOnce' : 'set'](name, value)
  amplitude.identify(identify)
}
